import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import {
  detectBrowserTimezone,
  getAllTimezones,
  formatTimezoneLabel,
  groupTimezonesByRegion,
  POPULAR_TIMEZONES,
  LABEL_FORMATS,
} from "../../utils/timezoneUtils";

/**
 * TimezoneSelect Component
 *
 * A comprehensive timezone selector with auto-detection, multiple display formats, DST support,
 * region grouping, and full Ant Design Select integration.
 *
 * @param {string} value - Selected timezone value
 * @param {function} onChange - Callback function when selection changes
 * @param {boolean} autoDetect - Auto-detect and select browser timezone (default: true)
 * @param {string} labelFormat - Label display format using LABEL_FORMATS constants (default: LABEL_FORMATS.NAME_WITH_OFFSET)
 *   Available formats: LABEL_FORMATS.CODE | NAME | OFFSET | NAME_WITH_OFFSET | NAME_WITH_UTC | ABBR | FULL | ABBR_WITH_OFFSET
 * @param {boolean} showSearch - Enable search functionality (default: true)
 * @param {boolean} groupByRegion - Group timezones by region (default: false)
 * @param {boolean} showPopularFirst - Show popular timezones at top (default: true)
 * @param {array} popularTimezones - Custom popular timezones list (default: POPULAR_TIMEZONES)
 * @param {array} regions - Filter by specific regions (e.g., ["Americas", "Europe"])
 * @param {function} filterTimezones - Custom filter function
 * @param {string} placeholder - Placeholder text (default: "Select Timezone")
 * @param {boolean} allowClear - Show clear button (default: true)
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disable the select
 * @param {object} style - Inline styles
 * @param {number|string} dropdownWidth - Custom width for dropdown (e.g., 400, "500px", "auto")
 * @param {string} dropdownAlign - Align dropdown to 'left' or 'right' of the select component
 */
const TimezoneSelect = ({
  value,
  onChange,
  autoDetect = false,
  labelFormat = LABEL_FORMATS.NAME_WITH_OFFSET,
  showSearch = true,
  groupByRegion = false,
  showPopularFirst = true,
  popularTimezones = POPULAR_TIMEZONES,
  regions = null,
  filterTimezones = null,
  placeholder = "Select Timezone",
  allowClear = true,
  className = "",
  disabled = false,
  style,
  dropdownWidth,
  dropdownAlign,
  ...rest
}) => {
  const [detectedTimezone, setDetectedTimezone] = useState(null);

  // Auto-detect browser timezone on mount
  useEffect(() => {
    if (autoDetect && !value) {
      const detected = detectBrowserTimezone();
      setDetectedTimezone(detected);
      onChange?.(detected);
    }
  }, [autoDetect, value, onChange]);

  // Get and process all timezones
  const processedTimezones = useMemo(() => {
    let timezones = getAllTimezones();

    // Apply region filter
    if (regions && Array.isArray(regions) && regions.length > 0) {
      timezones = timezones.filter(tz => regions.includes(tz.region));
    }

    // Apply custom filter
    if (filterTimezones && typeof filterTimezones === "function") {
      timezones = timezones.filter(filterTimezones);
    }

    // Format labels
    timezones = timezones.map(tz => ({
      ...tz,
      label: formatTimezoneLabel(tz, labelFormat),
    }));

    return timezones;
  }, [regions, filterTimezones, labelFormat]);

  // Separate popular and other timezones
  const { popularList, otherList } = useMemo(() => {
    if (!showPopularFirst) {
      return { popularList: [], otherList: processedTimezones };
    }

    const popular = [];
    const others = [];

    processedTimezones.forEach(tz => {
      if (popularTimezones.includes(tz.code)) {
        popular.push(tz);
      } else {
        others.push(tz);
      }
    });

    // Sort popular timezones by the order in popularTimezones array
    popular.sort((a, b) => {
      return popularTimezones.indexOf(a.code) - popularTimezones.indexOf(b.code);
    });

    return { popularList: popular, otherList: others };
  }, [processedTimezones, showPopularFirst, popularTimezones]);

  // Group timezones by region if needed
  const groupedTimezones = useMemo(() => {
    if (!groupByRegion) return null;
    return groupTimezonesByRegion(otherList);
  }, [groupByRegion, otherList]);

  // Filter function for search
  const filterOption = (input, option) => {
    const searchText = input.toLowerCase();
    const label = option?.label?.toLowerCase() || "";
    const value = option?.value?.toLowerCase() || "";
    return label.includes(searchText) || value.includes(searchText);
  };

  // Render options based on grouping
  const renderOptions = () => {
    const options = [];

    // Add popular timezones section
    if (showPopularFirst && popularList.length > 0) {
      options.push(
        <Select.OptGroup key="popular" label="Popular Timezones">
          {popularList.map(tz => (
            <Select.Option key={tz.code} value={tz.code} label={tz.label}>
              {tz.label}
            </Select.Option>
          ))}
        </Select.OptGroup>
      );
    }

    // Add grouped or flat timezone list
    if (groupByRegion && groupedTimezones) {
      // Sort regions alphabetically
      const sortedRegions = Object.keys(groupedTimezones).sort();

      sortedRegions.forEach(region => {
        const regionTimezones = groupedTimezones[region];
        if (regionTimezones.length > 0) {
          options.push(
            <Select.OptGroup key={region} label={region}>
              {regionTimezones.map(tz => (
                <Select.Option key={tz.code} value={tz.code} label={tz.label}>
                  {tz.label}
                </Select.Option>
              ))}
            </Select.OptGroup>
          );
        }
      });
    } else {
      // Flat list (no grouping)
      const timezonesToRender = showPopularFirst ? otherList : processedTimezones;

      if (!showPopularFirst || timezonesToRender.length > 0) {
        const optGroupContent = timezonesToRender.map(tz => (
          <Select.Option key={tz.code} value={tz.code} label={tz.label}>
            {tz.label}
          </Select.Option>
        ));

        if (showPopularFirst && timezonesToRender.length > 0) {
          options.push(
            <Select.OptGroup key="all" label="All Timezones">
              {optGroupContent}
            </Select.OptGroup>
          );
        } else {
          options.push(...optGroupContent);
        }
      }
    }

    return options;
  };

  // Prepare dropdown style if dropdownWidth is provided
  const dropdownStyle = dropdownWidth
    ? {
        minWidth: typeof dropdownWidth === "number" ? `${dropdownWidth}px` : dropdownWidth,
        maxWidth: typeof dropdownWidth === "number" ? `${dropdownWidth}px` : dropdownWidth,
      }
    : undefined;

  // Prepare dropdown align configuration
  const dropdownAlignConfig =
    dropdownAlign === "right"
      ? {
          points: ["tr", "br"], // Align top-right of dropdown to bottom-right of select
          offset: [0, 4],
          overflow: { adjustX: 1, adjustY: 1 },
        }
      : dropdownAlign === "left"
      ? {
          points: ["tl", "bl"], // Align top-left of dropdown to bottom-left of select
          offset: [0, 4],
          overflow: { adjustX: 1, adjustY: 1 },
        }
      : undefined;

  return (
    <Select
      showSearch={showSearch}
      className={`w-full ${className}`}
      allowClear={allowClear}
      value={value || detectedTimezone}
      onChange={onChange}
      placeholder={placeholder}
      optionFilterProp="label"
      filterOption={filterOption}
      disabled={disabled}
      style={style}
      dropdownStyle={dropdownStyle}
      dropdownAlign={dropdownAlignConfig}
      {...rest}
    >
      {renderOptions()}
    </Select>
  );
};

// Re-export LABEL_FORMATS for convenience
export { LABEL_FORMATS };

export default TimezoneSelect;
